/**
 * RadiusRenderer - Grid-based radius visualization
 * 
 * Renders radius indicators as faded grid squares instead of circles/boxes.
 * Maintains the grid-aligned aesthetic of the game.
 */

import Phaser from 'phaser';
import { TILE_SIZE } from '../types';

export interface RadiusRenderOptions {
  /** Center X position in world coordinates */
  centerX: number;
  /** Center Y position in world coordinates */
  centerY: number;
  /** Radius in tiles (not pixels) */
  radiusTiles: number;
  /** Fill color for the tiles */
  color: number;
  /** Base alpha (will fade towards edges) */
  alpha?: number;
  /** Whether to fade alpha towards the edge */
  fadeToEdge?: boolean;
  /** Whether to include the center tile */
  includeCenter?: boolean;
  /** Whether to only draw the outer ring */
  ringOnly?: boolean;
  /** Ring thickness in tiles (only used if ringOnly is true) */
  ringThickness?: number;
}

/**
 * Draw a grid-based radius indicator
 * Uses filled squares that fade towards the edge for a clean grid look
 */
export const drawGridRadius = (
  graphics: Phaser.GameObjects.Graphics,
  options: RadiusRenderOptions
): void => {
  const {
    centerX,
    centerY,
    radiusTiles,
    color,
    alpha = 0.3,
    fadeToEdge = true,
    includeCenter = false,
    ringOnly = false,
    ringThickness = 1,
  } = options;

  // Get the center tile
  const centerTileX = Math.floor(centerX / TILE_SIZE);
  const centerTileY = Math.floor(centerY / TILE_SIZE);

  // Iterate over all tiles in the bounding box
  for (let dy = -radiusTiles; dy <= radiusTiles; dy++) {
    for (let dx = -radiusTiles; dx <= radiusTiles; dx++) {
      // Skip center if not included
      if (!includeCenter && dx === 0 && dy === 0) {
        continue;
      }

      // Calculate distance from center (using Chebyshev distance for square radius)
      const chebyshevDist = Math.max(Math.abs(dx), Math.abs(dy));
      
      // Skip tiles outside radius
      if (chebyshevDist > radiusTiles) {
        continue;
      }

      // If ring only, skip tiles inside the ring
      if (ringOnly && chebyshevDist < radiusTiles - ringThickness + 1) {
        continue;
      }

      // Calculate alpha based on distance
      let tileAlpha = alpha;
      if (fadeToEdge) {
        // Fade from full alpha at center to lower alpha at edge
        const distRatio = chebyshevDist / radiusTiles;
        tileAlpha = alpha * (1 - distRatio * 0.6); // Keep at least 40% alpha at edge
      }

      // Calculate world position
      const worldX = (centerTileX + dx) * TILE_SIZE;
      const worldY = (centerTileY + dy) * TILE_SIZE;

      // Draw the tile
      graphics.fillStyle(color, tileAlpha);
      graphics.fillRect(worldX, worldY, TILE_SIZE, TILE_SIZE);
    }
  }
};

/**
 * Draw a grid-based radius indicator using Euclidean distance (circular shape)
 * Tiles are included if their center is within the radius
 */
export const drawGridRadiusCircular = (
  graphics: Phaser.GameObjects.Graphics,
  options: RadiusRenderOptions
): void => {
  const {
    centerX,
    centerY,
    radiusTiles,
    color,
    alpha = 0.3,
    fadeToEdge = true,
    includeCenter = false,
    ringOnly = false,
    ringThickness = 1,
  } = options;

  // Get the center tile
  const centerTileX = Math.floor(centerX / TILE_SIZE);
  const centerTileY = Math.floor(centerY / TILE_SIZE);

  // Iterate over all tiles in the bounding box
  for (let dy = -radiusTiles; dy <= radiusTiles; dy++) {
    for (let dx = -radiusTiles; dx <= radiusTiles; dx++) {
      // Skip center if not included
      if (!includeCenter && dx === 0 && dy === 0) {
        continue;
      }

      // Calculate Euclidean distance from center
      const euclideanDist = Math.sqrt(dx * dx + dy * dy);
      
      // Skip tiles outside radius
      if (euclideanDist > radiusTiles) {
        continue;
      }

      // If ring only, skip tiles inside the ring
      if (ringOnly && euclideanDist < radiusTiles - ringThickness) {
        continue;
      }

      // Calculate alpha based on distance
      let tileAlpha = alpha;
      if (fadeToEdge) {
        const distRatio = euclideanDist / radiusTiles;
        tileAlpha = alpha * (1 - distRatio * 0.6);
      }

      // Calculate world position
      const worldX = (centerTileX + dx) * TILE_SIZE;
      const worldY = (centerTileY + dy) * TILE_SIZE;

      // Draw the tile
      graphics.fillStyle(color, tileAlpha);
      graphics.fillRect(worldX, worldY, TILE_SIZE, TILE_SIZE);
    }
  }
};

/**
 * Draw combat range indicator - circular grid pattern
 * Shows the area where cannons can fire using Euclidean distance
 */
export const drawCombatRange = (
  graphics: Phaser.GameObjects.Graphics,
  centerX: number,
  centerY: number,
  rangeTiles: number,
  inRange: boolean
): void => {
  // Red when target is in range (danger!), orange when chasing
  const color = inRange ? 0xff0000 : 0xff6600;
  const alpha = inRange ? 0.35 : 0.2;

  // Draw filled grid tiles showing combat range (circular)
  drawGridRadiusCircular(graphics, {
    centerX,
    centerY,
    radiusTiles: rangeTiles,
    color,
    alpha,
    fadeToEdge: false,
    includeCenter: true,
    ringOnly: false,
  });
};

/**
 * Draw safe zone indicator for trading posts
 */
export const drawSafeZone = (
  graphics: Phaser.GameObjects.Graphics,
  centerX: number,
  centerY: number,
  radiusTiles: number,
  options?: {
    fillColor?: number;
    ringColor?: number;
  }
): void => {
  const fillColor: number = options?.fillColor ?? 0x87ceeb;
  const ringColor: number = options?.ringColor ?? 0x5dade2;

  // Draw filled tiles with cyan/blue color
  drawGridRadius(graphics, {
    centerX,
    centerY,
    radiusTiles,
    color: fillColor,
    alpha: 0.08,
    fadeToEdge: true,
    includeCenter: true,
    ringOnly: false,
  });

  // Draw outer ring slightly brighter
  drawGridRadius(graphics, {
    centerX,
    centerY,
    radiusTiles,
    color: ringColor,
    alpha: 0.15,
    fadeToEdge: false,
    includeCenter: false,
    ringOnly: true,
    ringThickness: 1,
  });
};

/**
 * Draw territory zone indicator for player-owned islands
 * Uses the owner's ship color for the territory visual
 * @param ownerColor - The ship color of the territory owner (hex number)
 * @param isOwnTerritory - Whether this is the local player's territory (more visible)
 */
export const drawTerritoryZone = (
  graphics: Phaser.GameObjects.Graphics,
  centerX: number,
  centerY: number,
  radiusTiles: number,
  ownerColor: number,
  isOwnTerritory: boolean
): void => {
  // Use owner's ship color for territory
  const color = ownerColor;
  
  // Darken the color slightly for the border
  const r = (ownerColor >> 16) & 0xff;
  const g = (ownerColor >> 8) & 0xff;
  const b = ownerColor & 0xff;
  const borderColor = ((Math.floor(r * 0.7) << 16) | (Math.floor(g * 0.7) << 8) | Math.floor(b * 0.7));
  
  // Draw filled tiles with territory color
  drawGridRadius(graphics, {
    centerX,
    centerY,
    radiusTiles,
    color,
    alpha: isOwnTerritory ? 0.15 : 0.08,
    fadeToEdge: true,
    includeCenter: true,
    ringOnly: false,
  });

  // Draw outer ring
  drawGridRadius(graphics, {
    centerX,
    centerY,
    radiusTiles,
    color: borderColor,
    alpha: isOwnTerritory ? 0.35 : 0.20,
    fadeToEdge: false,
    includeCenter: false,
    ringOnly: true,
    ringThickness: 1,
  });
};

/**
 * Draw interaction range indicator (for NPCs, objects, etc.)
 */
export const drawInteractionRange = (
  graphics: Phaser.GameObjects.Graphics,
  centerX: number,
  centerY: number,
  radiusTiles: number,
  color: number = 0x2ecc71 // Green by default
): void => {
  drawGridRadius(graphics, {
    centerX,
    centerY,
    radiusTiles,
    color,
    alpha: 0.12,
    fadeToEdge: true,
    includeCenter: false,
    ringOnly: false,
  });
};

/**
 * Draw aggro/combat range indicator for NPCs - circular pattern
 * Shows the NPC's firing range when aggroed
 */
export const drawAggroRange = (
  graphics: Phaser.GameObjects.Graphics,
  centerX: number,
  centerY: number,
  radiusTiles: number,
  isAggroed: boolean
): void => {
  // Red when aggroed and firing, orange when just detecting
  const color = isAggroed ? 0xff0000 : 0xff6600;
  const alpha = isAggroed ? 0.3 : 0.15;

  // Draw filled circular range (same style as player combat range)
  drawGridRadiusCircular(graphics, {
    centerX,
    centerY,
    radiusTiles,
    color,
    alpha,
    fadeToEdge: false,
    includeCenter: true,
    ringOnly: false,
  });
};
